import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { LAIKA_ACTION_ICONS, resolveActionStyleClass } from './laikaActionConfig';
import { inlineConfirmPrompt, openLaikaHref, resolveLaikaActionClick } from './laikaActionClick';
import { localizeLaikaActionLabel } from './localizeLaikaActionLabel';
import type { ActionButton } from './types';

interface LaikaActionPillsProps {
  actions: ActionButton[];
  onConsent: () => void;
  onReportBug?: () => void;
  onCloseMobile?: () => void;
  onSendMessage: (message: string) => void;
}

type PendingNavigate = {
  href: string;
  external: boolean;
  confirmMessage: string;
};

const LaikaActionPills: React.FC<LaikaActionPillsProps> = ({
  actions,
  onConsent,
  onReportBug,
  onCloseMobile,
  onSendMessage,
}) => {
  const { t } = useTranslation('messaging');
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingNavigate | null>(null);

  useEffect(() => {
    setPending(null);
  }, [actions]);

  const go = (href: string, external: boolean) => {
    openLaikaHref(href, external, {
      openWindow: window.open.bind(window),
      assignLocation: (url) => {
        window.location.href = url;
      },
      navigateInternal: (path) => {
        navigate(path);
        onCloseMobile?.();
      },
    });
  };

  const handleClick = (action: ActionButton) => {
    // R109 : unique entry — jamais navigate/open hors resolveLaikaActionClick
    const result = resolveLaikaActionClick(action, (key, options) =>
      t(key, options),
    );

    if (result.kind === 'consent') {
      onConsent();
      return;
    }

    if (result.kind === 'report_bug') {
      onReportBug?.();
      return;
    }

    if (result.kind === 'confirm_navigate') {
      setPending({
        href: result.href,
        external: result.external,
        confirmMessage: result.confirmMessage,
      });
      return;
    }

    if (result.kind === 'navigate') {
      go(result.href, result.external);
      return;
    }

    if (result.kind === 'message') {
      onSendMessage(result.text);
    }
  };

  if (pending) {
    return (
      <motion.div
        className="chat-action-buttons-container"
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      >
        <p className="chat-action-confirm-prompt">
          {inlineConfirmPrompt(pending.confirmMessage, t('chatWidget.confirmOpenFallback'))}
        </p>
        <div className="chat-action-buttons">
          <button
            type="button"
            className="chat-action-btn chat-action-btn-success"
            onClick={() => {
              go(pending.href, pending.external);
              setPending(null);
            }}
          >
            <span>{t('chatWidget.confirmOpenButton')}</span>
          </button>
          <button
            type="button"
            className="chat-action-btn chat-action-btn-default"
            onClick={() => setPending(null)}
          >
            <span>{t('chatWidget.confirmCancelButton')}</span>
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="chat-action-buttons-container"
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
    >
      <div className="chat-action-buttons">
        {actions.map((action, idx) => {
          const Icon = action.id ? LAIKA_ACTION_ICONS[action.id] : null;
          const styleClass = resolveActionStyleClass(action.style);
          const needsConfirm = action.requires_confirmation === true || action.auto_execute === true;

          return (
            <button
              key={`${action.id ?? action.label}-${idx}`}
              type="button"
              className={`chat-action-btn chat-action-btn-${styleClass}`}
              style={{ animationDelay: `${0.05 + idx * 0.05}s` }}
              title={needsConfirm ? t('chatWidget.confirmNeededHint') : undefined}
              onClick={() => handleClick(action)}
            >
              {Icon && <Icon size={14} className="chat-action-btn-icon" aria-hidden />}
              <span>{localizeLaikaActionLabel(action, t)}</span>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
};

export default LaikaActionPills;
